import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import { LOGIN_STEPS } from "~/screens/Auth/Login/constants";
import { useAuthStore } from "~/stores";
import { icons as baseIcons } from "~/ui/common/Icons";

type BackButtonProps = {
  to?: string | (() => void);
  className?: string;
  variant?: "default" | "violet";
  text?: string;
  handleGoBack?: () => void;
};

const icons = baseIcons.outline;

const backButtonStyles = tv({
  slots: {
    button:
      "top-[-50px] left-0 flex items-center gap-2 pb-4 font-semibold text-sm",
    iconContainer:
      "rounded-full border flex items-center justify-center size-5",
    icon: "size-6",
  },
  variants: {
    variant: {
      default: {
        button: "text-indigo",
        iconContainer: "border-indigo text-indigo",
      },
      violet: {
        button: "text-violet w-fit justify-start font-medium",
        iconContainer: "border-violet text-violet",
        icon: "text-violet size-3 stroke-2",
      },
    },
  },
});

const BackButton = ({
  to,
  className,
  variant = "default",
  handleGoBack,
  text = "Back",
}: BackButtonProps) => {
  const navigate = useNavigate();
  const setLoginStep = useAuthStore((store) => store.setLoginStep);

  const { button, iconContainer, icon } = backButtonStyles({ variant });

  const handleClick = (event: React.MouseEvent) => {
    if (!!handleGoBack) {
      event.preventDefault();
      handleGoBack();
      return;
    }

    setLoginStep(LOGIN_STEPS.EMAIL);
    if (!to) {
      event.preventDefault();
      navigate(-1);
    }

    if (typeof to === "function") {
      event.preventDefault();
      to();
      return;
    }
  };

  return (
    <Link
      to={typeof to === "string" ? to : "#"}
      onClick={handleClick}
      className={button({ className })}
      role="link"
    >
      <div className={iconContainer()}>
        <icons.ArrowLongLeftIcon className={icon()} />
      </div>
      {text}
    </Link>
  );
};

export default BackButton;
